import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import {
  BATTERY_WARRANTY_DATA,
  DEFAULT_VALUE,
  RANGE_AND_BATTERY_IDS,
  RANGE_AND_BATTERY_VALUES,
  TOOLTIPS_DATA,
  UNLIMITED,
} from 'site-modules/shared/components/inventory/ev-insights/ev-insights-constants';
import { get } from 'lodash-es';
import { numberWithCommas, numberWithCommasForString } from 'site-modules/shared/utils/string';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { getWarrantyMilesLeft, getWarrantyYearsLeft } from 'site-modules/shared/utils/inventory/warranty';

export const getBatteryRangeMap = ({ isNewVehicle, epaRange, batteryCapacityKWH }) => {
  const batteryRangeMap = [
    {
      id: isNewVehicle ? RANGE_AND_BATTERY_IDS.EPA_RANGE_ID_NEW : RANGE_AND_BATTERY_IDS.EPA_RANGE_ID_USED,
      trackingId: 'range_and_battery',
      trackingValue: isNewVehicle
        ? RANGE_AND_BATTERY_VALUES.EPA_RANGE_VALUE_NEW
        : RANGE_AND_BATTERY_VALUES.EPA_RANGE_VALUE_USED,
      summaryTitle: isNewVehicle
        ? RANGE_AND_BATTERY_VALUES.EPA_RANGE_VALUE_NEW
        : RANGE_AND_BATTERY_VALUES.EPA_RANGE_VALUE_USED,
      summaryValue: epaRange ? `${epaRange} mi` : DEFAULT_VALUE,
      description:
        'For new vehicles, this is the distance you can expect to travel on a full charge. EPA advises this range should be used for comparison purposes only. Actual range will vary depending on the condition of this vehicle’s battery pack, how you drive, driving conditions and other factors.',
    },
    {
      id: RANGE_AND_BATTERY_IDS.BATTERY_CAPACITY_ID,
      trackingId: 'range_and_battery',
      trackingValue: RANGE_AND_BATTERY_VALUES.BATTERY_CAPACITY,
      summaryTitle: RANGE_AND_BATTERY_VALUES.BATTERY_CAPACITY,
      summaryValue: batteryCapacityKWH ? `${batteryCapacityKWH} kWh` : DEFAULT_VALUE,
      description:
        'This value represents the amount of energy (in kilowatt-hours) the vehicle’s battery could store when new. A higher capacity generally means the vehicle can travel farther on a full charge. Over time and with use, actual battery capacity may decline due to battery degradation.',
    },
  ];

  return batteryRangeMap.filter(({ summaryValue }) => !!summaryValue);
};

function getWarrantyBatteryValueLeft(value, withCommas = false) {
  if (value === 0) return '';
  if (value === Number.MAX_SAFE_INTEGER) return UNLIMITED;

  return withCommas ? numberWithCommas(value) : String(value, '');
}

function getYearsAndMilesCopy(vinData, warrantyItem) {
  const yearsLeft = getWarrantyYearsLeft(warrantyItem, vinData);
  const milesLeft = getWarrantyMilesLeft(warrantyItem, vinData);
  return { yearsCopy: getWarrantyBatteryValueLeft(yearsLeft), milesCopy: getWarrantyBatteryValueLeft(milesLeft, true) };
}

function getWarrantyBatteryText(vinData, warrantyItem) {
  const { yearsCopy, milesCopy } = getYearsAndMilesCopy(vinData, warrantyItem);
  if (!yearsCopy || !milesCopy) return '';
  return `${yearsCopy} yrs or ${milesCopy} mi left`;
}

const defaultWarrantyObject = {
  showBatteryWarrantyModule: false,
  warrantyHeader: '',
  warrantyText: '',
  warrantyTooltip: '',
  warrantyDisclaimer: '',
};

export function getBatteryWarrantyData(evInsightsData, vinData) {
  const batteryWarranty = get(evInsightsData, 'style.batteryWarranty');
  const hybridComponentsWarranty = get(evInsightsData, 'style.hybridComponentsWarranty');
  const currentWarranty = batteryWarranty || hybridComponentsWarranty;
  if (!currentWarranty) return defaultWarrantyObject;
  const isNewVehicle = isNew(get(vinData, 'type', ''));
  const warrantyType = get(currentWarranty, 'warrantyType');

  if (isNewVehicle) {
    const { maxYears, maxMileage } = currentWarranty;
    if (!maxYears || !maxMileage) return defaultWarrantyObject;
    return {
      showBatteryWarrantyModule: true,
      warrantyHeader: BATTERY_WARRANTY_DATA.WARRANTY_HEADER_NEW,
      warrantyText: `${maxYears} yrs or ${numberWithCommasForString(maxMileage)} mi`,
      warrantyTooltip: batteryWarranty
        ? BATTERY_WARRANTY_DATA.WARRANTY_TOOLTIP
        : BATTERY_WARRANTY_DATA.HYBRID_WARRANTY_TOOLTIP,
      warrantyDisclaimer: '',
      warrantyType,
    };
  }

  const warrantyText = getWarrantyBatteryText(vinData, currentWarranty);

  if (warrantyText) {
    return {
      showBatteryWarrantyModule: true,
      warrantyHeader: BATTERY_WARRANTY_DATA.WARRANTY_HEADER_USED,
      warrantyText,
      warrantyTooltip: batteryWarranty
        ? BATTERY_WARRANTY_DATA.WARRANTY_TOOLTIP
        : BATTERY_WARRANTY_DATA.HYBRID_WARRANTY_TOOLTIP,
      warrantyDisclaimer: BATTERY_WARRANTY_DATA.WARRANTY_DISCLAIMER,
      warrantyType,
    };
  }

  return defaultWarrantyObject;
}

export const handleTooltipOpen = ({ id }) => {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_VIEW_CONTENT,
      subaction_name: TrackingConstant.ACTION_VIEW_CONTENT,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
      creative_id: TOOLTIPS_DATA[id].creativeId,
      value: TOOLTIPS_DATA[id].value,
    },
  });
};
